import * as React from 'react';
import { useTranslations } from '../../../store/translations/hooks';
import { TextInputField } from '../../../components/Forms/fields';
import { TK } from '../../../store/translations/translationKeys';
import { ClientsLoginSettingsData, FeatureEnum, MintuSettingsData } from '../../../models';
import { Alert } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getFeatures, getLoggedUserCompanies } from '../../../store/session/selectors';
import CollapsableItem from '../../../components/CollapsableItem';
import styled from 'styled-components';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import { TestAIChat } from './TestAIChat';
import { alertSuccess } from '../../../store/alerts/actions';
import AppButton from '../../../components/AppButton';

const WhatsAppCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    padding: var(--spacer-S);
    background-color: var(--bg-light);
    border: var(--border-thin) solid var(--appGreen-hover);
    border-radius: 8px;
`;

const WhatsAppUrlBox = styled.div`
    display: flex;
    gap: var(--spacer-XS);
    align-items: center;
`;

const WhatsAppUrlText = styled.code`
    flex: 1;
    padding: var(--spacer-XS) var(--spacer-S);
    background-color: #fff;
    border: var(--border-thin) solid var(--appGreen);
    border-radius: 4px;
    font-size: 0.85em;
    word-break: break-all;
    color: var(--text-body);
`;

const WhatsAppWidget = styled.a<React.AnchorHTMLAttributes<HTMLAnchorElement>>`
    display: inline-flex;
    align-items: center;
    gap: var(--spacer-XS);
    padding: var(--spacer-XS) var(--spacer-S);
    border-radius: 999px;
    text-decoration: none;
    font-weight: 600;
    color: white;
    background: linear-gradient(135deg, var(--appGreen), var(--appGreen-hover));
`;

const ProminentCollapsableWrapper = styled.div`
    .collapsableItemHeaderSmall {
        font-weight: 600;
        font-size: 1.12em;
        color: var(--text-body);
    }
`;

interface MintuTabProps {
    id: string;
    values: ClientsLoginSettingsData;
    onMintuChanged: (mintu: Partial<MintuSettingsData>) => void;
}

export const MintuTab: React.FC<MintuTabProps> = ({ id, values, onMintuChanged }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const features = useSelector(getFeatures);
    const [companyId] = useSelector(getLoggedUserCompanies);

    const hasAI = features.includes(FeatureEnum.ArtificialInteligence);
    const isMintuEnabled = hasAI;
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const whatsAppRedirectUrl = `${apiBaseUrl}/v1/whatsapp/url/${companyId}`;
    const whatsAppQrUrl = `${apiBaseUrl}/v1/whatsapp/url/${companyId}/qr`;
    const whatsAppEmbedCode = `<a href="${whatsAppRedirectUrl}" target="_blank" rel="noopener noreferrer">${t(TK.mintuWhatsAppOpenInWhatsApp)}</a>`;

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
            <Alert color="info" isOpen>
                {t(TK.mintuDescription)}
            </Alert>

            {!isMintuEnabled && (
                <Alert color="warning" isOpen>
                    {t(TK.mintuNotEnabledWarning)}
                </Alert>
            )}

            {isMintuEnabled && (
                <div style={{display: 'flex', gap: 'var(--spacer-S)', alignItems: 'center'}}>
                    <img src="/mintu.png" alt="Mintu" style={{width: 72, height: 72, flexShrink: 0}} />
                    <div style={{flex: 1}}>
                        <TextInputField
                            id={`${id}-mintu-custom-instructions`}
                            label={t(TK.mintuCustomInstructions)}
                            helpText={t(TK.mintuCustomInstructionsHelp)}
                            value={values?.mintu?.customInstructions ?? ''}
                            onChange={customInstructions => onMintuChanged({customInstructions})}
                            type='textarea'
                        />
                    </div>
                </div>
            )}

            {isMintuEnabled && (
                <ProminentCollapsableWrapper>
                <CollapsableItem title={<><WhatsAppIcon style={{fontSize: '1.1em', verticalAlign: 'middle', marginRight: 'var(--spacer-XS)'}} />{t(TK.mintuWhatsAppSection)}</>} size='sm'>
                    <WhatsAppCard>
                        <span style={{color: 'var(--text-muted)', fontSize: '0.9em'}}>{t(TK.mintuWhatsAppSectionDescription)}</span>
                        <div style={{display: 'flex', gap: 'var(--spacer-M)', alignItems: 'flex-start', flexWrap: 'wrap'}}>
                            <div style={{flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)'}}>
                                <WhatsAppUrlBox>
                                    <WhatsAppUrlText>{whatsAppRedirectUrl}</WhatsAppUrlText>
                                    <AppButton
                                        color="secondary"
                                        size="sm"
                                        onClick={() => { navigator.clipboard.writeText(whatsAppRedirectUrl); dispatch(alertSuccess(t(TK.copiedToClipboard))); }}>
                                        {t(TK.copy)}
                                    </AppButton>
                                </WhatsAppUrlBox>
                                <WhatsAppUrlBox>
                                    <WhatsAppUrlText>{whatsAppEmbedCode}</WhatsAppUrlText>
                                    <AppButton
                                        color="secondary"
                                        size="sm"
                                        onClick={() => { navigator.clipboard.writeText(whatsAppEmbedCode); dispatch(alertSuccess(t(TK.copiedToClipboard))); }}>
                                        {t(TK.mintuWhatsAppCopyEmbedCode)}
                                    </AppButton>
                                </WhatsAppUrlBox>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)', alignItems: 'center'}}>
                                <img src={whatsAppQrUrl} alt={t(TK.mintuWhatsAppQrCodeAlt)} style={{width: 120, height: 120, border: 'var(--border-thin) solid var(--appGreen)', borderRadius: 4}} />
                                <a href={whatsAppQrUrl} download="whatsapp-qr.png" style={{fontSize: '0.85em', color: 'var(--appBlue)'}}>
                                    {t(TK.mintuWhatsAppScanQrCode)}
                                </a>
                                <WhatsAppWidget
                                    href={whatsAppRedirectUrl}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    <WhatsAppIcon style={{fontSize: 20}} />
                                    {t(TK.mintuWhatsAppOpenInWhatsApp)}
                                </WhatsAppWidget>
                            </div>
                        </div>
                    </WhatsAppCard>
                </CollapsableItem>
                </ProminentCollapsableWrapper>
            )}

            {hasAI && (
                <ProminentCollapsableWrapper>
                <CollapsableItem title={<><ChatBubbleOutlineIcon style={{fontSize: '1.1em', verticalAlign: 'middle', marginRight: 'var(--spacer-XS)'}} />{t(TK.experimentMintuNow)}</>} size='sm'>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
                        <span style={{color: 'var(--text-muted)', fontSize: '0.9em'}}>{t(TK.experimentMintuNowDescription)}</span>
                        <TestAIChat/>
                    </div>
                </CollapsableItem>
                </ProminentCollapsableWrapper>
            )}
        </div>
    );
};
